import React from 'react';
import { AuthService } from '../../../../service/AuthService';
import { Namespace } from '../../../../types/namespace';
import type { VectorEmbeddingModel } from '../../../../types/vector';
import { Space } from '../../../../util/Types/Sidebar';
import ObjectModal from '../../../Layout/LayoutComponents/FluxObjects/ObjectModal';
import { GeneratedAudioPreviewModal } from '../../../Layout/LayoutComponents/GeneratedAudio/GeneratedAudioPreviewModal';
import { GeneratedFilePreviewModal } from '../../../Layout/LayoutComponents/GeneratedFile/GeneratedFilePreviewModal';
import { GeneratedImagePreviewModal } from '../../../Layout/LayoutComponents/GeneratedImages/GeneratedImagePreviewModal';
import { DeleteNamespaceDialog } from '../../../UI/DeleteNamespaceDialog';
import { VectorMemoryModal } from '../../../UI/VectorMemoryModal';
import { FluxObject } from '../AgentsSidebar/context';
import { CreateItemModal } from '../CreateItemModal';
import { SidebarFolderModal } from '../shared/SidebarFolderModal';
import type { useSidebarFolderActions } from '../shared/useSidebarFolderActions';
import type { useVectorMemoryDeletion } from '../shared/useVectorMemoryDeletion';
import type { CreateItemType } from '../types';
import { RenameAssetModal } from './RenameAssetModal';
import type { useAssetHandlers } from './hooks/useAssetHandlers';

type AssetHandlers = ReturnType<typeof useAssetHandlers>;
type FolderActions = ReturnType<typeof useSidebarFolderActions>;
type VectorMemoryDeletion = ReturnType<typeof useVectorMemoryDeletion>;

interface DataSidebarModalsProps {
  spaces: Space[];
  isDarkMode: boolean;
  modals: AssetHandlers['modals'];
  dispatch: AssetHandlers['dispatch'];
  renameModal: AssetHandlers['renameModal'];
  folderActions: FolderActions;
  deleteVectorMemory: VectorMemoryDeletion['deleteVectorMemory'];
  refresh: () => void;
  resetObjectUpdate: () => void;
  setAuthUser: (user: unknown) => void;
  createItemRequest: {
    type: CreateItemType;
    folderId?: string;
    spaceId?: string;
  } | null;
  setCreateItemRequest: (
    value: { type: CreateItemType; folderId?: string; spaceId?: string } | null,
  ) => void;
  vectorCreateRequest: { spaceId?: string } | null;
  setVectorCreateRequest: (value: { spaceId?: string } | null) => void;
  editingObject: FluxObject | null;
  setEditingObject: (value: FluxObject | null) => void;
  editingVector: Namespace | null;
  setEditingVector: (value: Namespace | null) => void;
  pendingVectorNamespace: Namespace | null;
  setPendingVectorNamespace: (value: Namespace | null) => void;
}

const ASSET_TYPE_LABELS: Record<string, string> = {
  image: 'Image',
  audio: 'Audio',
  file: 'File',
};

export const DataSidebarModals: React.FC<DataSidebarModalsProps> = ({
  spaces,
  isDarkMode,
  modals,
  dispatch,
  renameModal,
  folderActions,
  deleteVectorMemory,
  refresh,
  resetObjectUpdate,
  setAuthUser,
  createItemRequest,
  setCreateItemRequest,
  vectorCreateRequest,
  setVectorCreateRequest,
  editingObject,
  setEditingObject,
  editingVector,
  setEditingVector,
  pendingVectorNamespace,
  setPendingVectorNamespace,
}) => {
  const { imagePreview, audioPreview, filePreview } = modals;

  return (
    <>
      {createItemRequest && (
        <CreateItemModal
          show
          onHide={() => setCreateItemRequest(null)}
          type={createItemRequest.type}
          initialFolderId={createItemRequest.folderId}
          initialSpaceId={createItemRequest.spaceId}
        />
      )}
      {vectorCreateRequest && (
        <VectorMemoryModal
          open
          spaceId={vectorCreateRequest.spaceId}
          onOpenChange={async ({ open }) => {
            if (!open) {
              setVectorCreateRequest(null);
              const userRes = await AuthService.getMe();
              setAuthUser(userRes?.data);
              refresh();
            }
          }}
        />
      )}
      {editingObject && (
        <ObjectModal
          show
          onHide={async () => {
            setEditingObject(null);
            resetObjectUpdate();
            refresh();
          }}
          spaceId={
            spaces.find(s => s.objects?.some(o => o.id === editingObject.id))
              ?.id ?? 'user'
          }
        />
      )}
      {editingVector && (
        <VectorMemoryModal
          open
          namespaceId={editingVector.id}
          spaceId={
            spaces.find(s => s.namespaces?.some(n => n.id === editingVector.id))
              ?.id
          }
          onOpenChange={async ({ open }) => {
            if (!open) {
              setEditingVector(null);
              refresh();
            }
          }}
        />
      )}
      <GeneratedImagePreviewModal
        image={imagePreview.image}
        open={imagePreview.show}
        onClose={() => dispatch({ type: 'CLOSE_IMAGE_PREVIEW' })}
      />
      <GeneratedAudioPreviewModal
        audio={audioPreview.audio}
        open={audioPreview.show}
        onClose={() => dispatch({ type: 'CLOSE_AUDIO_PREVIEW' })}
      />
      <GeneratedFilePreviewModal
        file={filePreview.file}
        open={filePreview.show}
        onClose={() => dispatch({ type: 'CLOSE_FILE_PREVIEW' })}
      />
      <RenameAssetModal
        show={renameModal.show}
        currentName={renameModal.currentName}
        assetType={ASSET_TYPE_LABELS[renameModal.type ?? ''] ?? 'File'}
        onHide={renameModal.onHide}
        onRename={renameModal.onRename}
      />
      {pendingVectorNamespace ? (
        <DeleteNamespaceDialog
          open
          embeddingModelName={
            (pendingVectorNamespace.embeddingModelName ||
              'voyage-3-large') as VectorEmbeddingModel
          }
          id={pendingVectorNamespace.id}
          name={pendingVectorNamespace.name}
          onOpenChange={open => {
            if (!open) setPendingVectorNamespace(null);
          }}
          onNamespaceDelete={({
            embeddingModelName,
            namespaceId,
            namespaceName,
          }) => {
            void deleteVectorMemory({
              id: namespaceId,
              name: namespaceName,
              embeddingModelName,
            } as Namespace).then(() => setPendingVectorNamespace(null));
          }}
        />
      ) : null}
      <SidebarFolderModal
        actions={folderActions}
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </>
  );
};
